import { useEffect, useState } from 'react'
import { Badge, BlockStack, Box, Button, Card, Checkbox, FormLayout, Icon, InlineGrid, InlineStack, Page, RadioButton, Text, TextField } from '@shopify/polaris'
import { XCircleIcon } from '@shopify/polaris-icons'
import { useDispatch, useSelector } from 'react-redux'
import { setDefaultPrice } from "../../store/actions/shippingActions";
import constants from '../../store/constants';
import useAuthenticatedFetch from '../../hooks/useAuthenticatedFetch';

const FLOW = [
    { step: 'Step 1', title: 'Customer reaches checkout', text: 'The app checks their address and cart.' },
    { step: 'Step 2', title: 'No shipping rate matches', text: "e.g. an area or weight you haven't set up." },
    { step: 'Step 3', title: 'Fallback rate is shown', text: 'So the customer can still finish their order.', highlight: true }
]

/** "No shipping options" checkout mock-up. */
const NoOptions = ({ note }) => (
    <Box background="bg-surface-secondary" borderRadius="200" borderWidth="025" borderColor="border" padding="400" minHeight="120px">
        <BlockStack gap="100" inlineAlign="center">
            <Icon source={XCircleIcon} tone="subdued" />
            <Text as="p" alignment="center" tone="subdued">No shipping options from this app</Text>
            {note ? <Text as="p" alignment="center" variant="bodySm" tone="subdued">{note}</Text> : null}
        </BlockStack>
    </Box>
)

/** One checkout mock-up card; the one matching the current state gets a dark outline. */
const Scenario = ({ current, badge, children }) => (
    <div style={{ opacity: current ? 1 : 0.6, height: '100%' }}>
        <Box background="bg-surface" borderRadius="300" borderWidth={current ? '050' : '025'} borderColor={current ? 'border-emphasis' : 'border'} padding="400" minHeight="100%">
            <BlockStack gap="300">
                <InlineStack gap="200" blockAlign="center">
                    {badge}
                    {current ? <Text as="span" variant="bodySm" fontWeight="semibold" tone="subdued">← now</Text> : null}
                </InlineStack>
                <Text as="h3" variant="headingSm">Shipping method</Text>
                {children}
            </BlockStack>
        </Box>
    </div>
)

const DefaultPrice = () => {
    const { shop_id, currency, features } = useSelector(state => state.ConfigReducer)
    const { defaultPrice } = useSelector(state => state.ShippingReducer)

    const dispatch = useDispatch()
    const fetchJson = useAuthenticatedFetch()

    const [defaultP] = useState(features.indexOf(constants.DEFAULT_PRICE) >= 0)
    const [Price, setPrice] = useState({ name: '', value: '', service_code: null, description: null, shop_id: shop_id, active: false })
    const [IsSaving, setIsSaving] = useState(false)
    const [Error, setError] = useState({})
    const [setupOpen, setSetupOpen] = useState(false)

    useEffect(() => {
        if (shop_id !== '') {
            setPrice(p => ({ ...p, shop_id: shop_id }))
        }
    }, [shop_id])

    useEffect(() => {
        if (Object.keys(defaultPrice).length > 0) {
            setPrice(defaultPrice)
        }
    }, [defaultPrice])

    const configured = Boolean(Price.id)
    const showForm = configured || setupOpen
    const isOn = configured && Boolean(Price.active)

    const handleChange = obj => setPrice(p => ({ ...p, ...obj }))

    const savePrice = async (overrides = {}) => {
        const data = { ...Price, ...overrides }
        if (!window.navigator.onLine) return
        const error = {}
        if (!data.name || data.name === '') error.name = 'Required!'
        if (data.value === undefined || data.value === null || data.value === '') error.value = 'Required!'
        else if (data.value < 0) error.value = 'Price must be 0 (Zero) or greater!'
        setError(error)
        if (Object.keys(error).length) return

        setIsSaving(true)
        try {
            const price = await fetchJson(`/settings/price`, {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...data, shop_id: shop_id })
            })
            dispatch(setDefaultPrice(price))
        } catch (error) {
            console.log(error);
        } finally {
            setIsSaving(false)
        }
    }

    const proSuffix = defaultP ? '' : ' (Pro)'
    const hasPrice = Price.value !== '' && Price.value !== null && Price.value !== undefined
    const priceShown = hasPrice ? `${Price.value} ${currency}` : '[Price]'

    return (
        <Page
            title="Fallback Shipping Rate"
            subtitle="This price will be shown on checkout if no criteria matched."
            titleMetadata={<Badge tone={configured ? 'success' : 'attention'}>{configured ? 'Configured' : 'Not-Configured'}</Badge>}
            primaryAction={configured ? { content: 'Update', loading: IsSaving, disabled: IsSaving || !defaultP, onAction: () => savePrice() } : undefined}
        >
            <BlockStack gap="500">
                {/* How it works */}
                <Card>
                    <BlockStack gap="300">
                        <Text as="h2" variant="headingMd">Your safety net at checkout</Text>
                        <InlineGrid columns={{ xs: 1, md: 3 }} gap="300">
                            {FLOW.map(f => (
                                <Box key={f.step} background={f.highlight ? 'bg-surface-info' : 'bg-surface-secondary'} borderRadius="200" padding="400" borderWidth={f.highlight ? '025' : undefined} borderColor={f.highlight ? 'border-info' : undefined}>
                                    <BlockStack gap="100">
                                        <Text as="span" variant="bodySm" fontWeight="semibold" tone="subdued">{f.step}</Text>
                                        <Text as="h3" variant="headingSm">{f.title}</Text>
                                        <Text as="p" variant="bodySm" tone="subdued">{f.text}</Text>
                                    </BlockStack>
                                </Box>
                            ))}
                        </InlineGrid>
                    </BlockStack>
                </Card>

                {!showForm ? (
                    /* Not set up yet */
                    <Card>
                        <InlineGrid columns={{ xs: 1, md: 2 }} gap="600" alignItems="center">
                            <BlockStack gap="300" inlineAlign="start">
                                <Badge tone="attention">Not set up yet</Badge>
                                <Text as="h2" variant="headingLg">You don't have a fallback rate yet</Text>
                                <Text as="p" tone="subdued">Right now, customers whose address or cart doesn't match any of your rates see no shipping option from this app. Add a fallback rate so they can still check out.</Text>
                                <InlineStack gap="300" blockAlign="center">
                                    <Button variant="primary" onClick={() => setSetupOpen(true)}>Set up fallback rate</Button>
                                    <Button variant="plain" url="/tutorials">Watch how it works</Button>
                                </InlineStack>
                            </BlockStack>
                            <BlockStack gap="200">
                                <Text as="p" variant="bodySm" fontWeight="semibold" tone="subdued">What these customers see today</Text>
                                <NoOptions />
                            </BlockStack>
                        </InlineGrid>
                    </Card>
                ) : (
                    /* Form + before / after */
                    <InlineGrid columns={{ xs: 1, md: ['oneThird', 'twoThirds'] }} gap="500" alignItems="start">
                        <Card>
                            <BlockStack gap="400">
                                <Text as="h2" variant="headingMd">Set Price</Text>
                                <FormLayout>
                                    <TextField label="Display Name" autoComplete="off" disabled={!defaultP} error={Error.name} placeholder={`e.g. Standard Shipping${proSuffix}`} value={Price.name ?? ''} onChange={value => handleChange({ name: value })} />
                                    <TextField label="Price" type="number" min={0} autoComplete="off" disabled={!defaultP} error={Error.value} placeholder={`00.0${proSuffix}`} value={hasPrice ? String(Price.value) : ''} suffix={currency} onChange={value => handleChange({ value })} />
                                    <TextField label="Description" autoComplete="off" disabled={!defaultP} helpText="Optional note under the name." placeholder="Write description here" value={Price.description ?? ''} onChange={value => handleChange({ description: value })} />
                                    {configured ? (
                                        <Checkbox
                                            label="Active"
                                            disabled={!defaultP}
                                            checked={Boolean(Price.active)}
                                            helpText={Price.active ? 'Price will be shown on checkout.' : 'Off — nothing is shown when no rate matches.'}
                                            onChange={value => handleChange({ active: value })}
                                        />
                                    ) : null}
                                </FormLayout>
                                {!configured ? (
                                    <InlineStack gap="200" align="end">
                                        <Button variant="tertiary" onClick={() => setSetupOpen(false)}>Cancel</Button>
                                        <Button variant="primary" loading={IsSaving} disabled={IsSaving || !defaultP} onClick={() => savePrice({ active: true })}>Save &amp; turn on</Button>
                                    </InlineStack>
                                ) : null}
                            </BlockStack>
                        </Card>

                        <BlockStack gap="300">
                            <Text as="h2" variant="headingMd">What a customer with no matching rate sees</Text>
                            <InlineGrid columns={{ xs: 1, sm: 2 }} gap="400">
                                <Scenario current={!isOn} badge={<Badge>{configured ? 'Fallback rate off' : 'Not set up yet'}</Badge>}>
                                    <NoOptions note="The customer may not be able to complete checkout." />
                                </Scenario>
                                <Scenario current={isOn} badge={<Badge tone="success">{configured ? 'Fallback rate on' : 'After you save'}</Badge>}>
                                    <Box background="bg-surface-info" borderRadius="200" borderWidth="050" borderColor="border-info" padding="300">
                                        <InlineStack align="space-between" blockAlign="start" gap="200" wrap={false}>
                                            <RadioButton
                                                label={Price.name || 'Standard Shipping'}
                                                helpText={Price.description || 'Description appears here'}
                                                checked
                                                onChange={() => { }}
                                                id="fallback-preview"
                                                name="fallback-preview"
                                            />
                                            <Text as="span" fontWeight="bold">{priceShown}</Text>
                                        </InlineStack>
                                    </Box>
                                </Scenario>
                            </InlineGrid>
                            <InlineStack><Button variant="plain" url="/tutorials">Watch: How to add Shipping Rate</Button></InlineStack>
                        </BlockStack>
                    </InlineGrid>
                )}
            </BlockStack>
        </Page>
    )
}

export default DefaultPrice
